import { useState, useLayoutEffect } from 'react';

/**
 * useTipClamp — shared horizontal clamping for floating tooltips.
 *
 * Measures `tipRef.current` after paint, walks up the DOM finding any
 * ancestor with non-visible overflow-x, and returns a `shiftX` pixel
 * offset that callers should add to their tooltip's horizontal
 * transform. Guarantees the tooltip never overflows the nearest
 * clipping container (modal body, chart card, etc.) nor the viewport.
 *
 * Why walk ancestors instead of using window bounds?
 * Tooltips sit INSIDE modals that visually clip their overflow well
 * before the tooltip reaches the window edge, causing:
 *   • tooltip appears cut off inside the modal,
 *   • a scrollbar pops into existence at the bottom of the modal.
 * Measuring against the first clipping ancestor catches both cases.
 *
 * Returns: number — delta to add to the tooltip's translateX.
 *   0 when the tooltip fits centered.
 *   Negative when tooltip was overflowing the right edge → shift left.
 *   Positive when tooltip was overflowing the left edge → shift right.
 *
 * Usage:
 *   const tipRef = useRef(null);
 *   const shiftX = useTipClamp(tipRef, open, [label, hoverX]);
 *   // …
 *   <div ref={tipRef} style={{ transform: `translate(calc(-50% + ${shiftX}px), 0)` }} />
 */
export default function useTipClamp(tipRef, open, deps = []) {
  const [shiftX, setShiftX] = useState(0);

  useLayoutEffect(() => {
    if (!open) {
      if (shiftX !== 0) setShiftX(0);
      return;
    }
    const el = tipRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;

    let safeLeft = 0;
    let safeRight = window.innerWidth;
    let node = el.parentElement;
    while (node && node !== document.body) {
      const s = getComputedStyle(node);
      // A clipping ancestor in EITHER direction establishes a scroll
      // container on BOTH axes (per CSS overflow spec). Radix's
      // DialogContent uses only `overflow-y: auto` but its horizontal
      // overflow is still clipped at the modal edge — so we must treat
      // overflow-y !== visible as a horizontal bound too.
      const clipsX = /(auto|scroll|hidden|clip)/.test(s.overflowX)
                  || /(auto|scroll|hidden|clip)/.test(s.overflow);
      const clipsY = /(auto|scroll|hidden|clip)/.test(s.overflowY);
      if (clipsX || clipsY) {
        const r = node.getBoundingClientRect();
        if (r.left > safeLeft) safeLeft = r.left;
        if (r.right < safeRight) safeRight = r.right;
      }
      node = node.parentElement;
    }

    let delta = 0;
    if (rect.right > safeRight - pad) {
      delta = safeRight - pad - rect.right;
    } else if (rect.left < safeLeft + pad) {
      delta = safeLeft + pad - rect.left;
    }
    if (Math.abs(delta) > 0.5) {
      // Functional update + omitting shiftX from deps → no render loop.
      setShiftX((s) => s + delta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ...deps]);

  return shiftX;
}
